import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { X, Check, RotateCw } from "lucide-react";

/**
 * Photo crop modal. Receives a source URL/blob, returns a cropped JPEG blob
 * through onCropped(blob). Powered by react-easy-crop.
 */
export default function PhotoCropper({ src, aspect = 3 / 4, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, areaPx) => setPixels(areaPx), []);

  const handleConfirm = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(src, pixels, rotation);
      onCropped(blob);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3" data-testid="cropper-modal">
      <div className="w-full max-w-md bg-stone-950 border border-stone-800 rounded-xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
          <h4 className="text-sm font-medium text-stone-100">Crop photo</h4>
          <button onClick={onCancel} className="p-1.5 rounded hover:bg-stone-800" data-testid="cropper-close"><X className="w-4 h-4 text-stone-400" /></button>
        </div>
        <div className="relative bg-stone-900 w-full" style={{ height: 380 }}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            objectFit="contain"
          />
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-stone-500">Zoom</label>
            <input type="range" min={1} max={3} step={0.05} value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="w-full accent-amber-500"
              data-testid="cropper-zoom" />
          </div>
          <div className="flex items-center justify-between">
            <button
              onClick={() => setRotation((r) => (r + 90) % 360)}
              className="inline-flex items-center gap-1.5 text-xs text-stone-300 hover:text-amber-300"
              data-testid="cropper-rotate"
            ><RotateCw className="w-3.5 h-3.5" /> Rotate</button>
            <button
              onClick={handleConfirm}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500 text-stone-950 text-sm font-semibold hover:bg-amber-400 disabled:opacity-50"
              data-testid="cropper-confirm"
            ><Check className="w-4 h-4" /> {busy ? "Saving..." : "Apply"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Helper — actually performs the canvas crop and returns a JPEG blob.
// =============================================================================
async function getCroppedBlob(src, pixels, rotation = 0) {
  const image = await loadImage(src);
  const radians = (rotation * Math.PI) / 180;
  // Bounding box of rotated image
  const bbW = Math.abs(Math.cos(radians) * image.width) + Math.abs(Math.sin(radians) * image.height);
  const bbH = Math.abs(Math.sin(radians) * image.width) + Math.abs(Math.cos(radians) * image.height);

  const safe = document.createElement("canvas");
  safe.width = bbW; safe.height = bbH;
  const sctx = safe.getContext("2d");
  sctx.translate(bbW / 2, bbH / 2);
  sctx.rotate(radians);
  sctx.drawImage(image, -image.width / 2, -image.height / 2);

  const out = document.createElement("canvas");
  out.width = pixels.width; out.height = pixels.height;
  const ctx = out.getContext("2d");
  ctx.drawImage(
    safe,
    pixels.x + (bbW - image.width) / 2,
    pixels.y + (bbH - image.height) / 2,
    pixels.width, pixels.height,
    0, 0, pixels.width, pixels.height
  );
  return await new Promise((res) => out.toBlob((b) => res(b), "image/jpeg", 0.92));
}

function loadImage(src) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}
